import { Html, Text } from 'slate'
import { editTablePlugin } from './Table'

const IGNORE_TAGS = ['head', 'meta', 'title', 'style']

const IGNORE_CONTENT = [/^\s+$/]

const BLOCK_TAGS = {
  div: 'div',
  li: 'list_item',
  ul: 'ul_list',
  ol: 'ol_list',
  blockquote: 'quote',
  pre: 'code',
  h1: 'heading_1',
  h2: 'heading_2',
  h3: 'heading_3',
  h4: 'heading_4',
  h5: 'heading_5',
  h6: 'heading_6',
  table: 'table',
  thead: 'thead',
  tbody: 'tbody',
  tr: 'table_row',
  td: 'table_cell',
}

const MARK_TAGS = {
  b: 'bold',
  strong: 'bold',
  em: 'italic',
  i: 'italic',
  u: 'underline',
  s: 'strikethrough',
  code: 'code',
}

const NullNode = () => ({
  kind: 'block',
  type: 'null',
  isVoid: true,
  nodes: [],
})

const processText = text =>
  text.replace(/[\t]+/g, ' ').replace(/[\r\n]+/g, '\n')

const RULES = [
  //Ignore tags and content
  {
    deserialize(el) {
      if (
        IGNORE_TAGS.includes(el.name) ||
        IGNORE_CONTENT.some(exp => exp.test(el.data))
      ) {
        return NullNode()
      }
    },
  },
  //Font tags to marks
  {
    deserialize(el, next) {
      if (el.name !== 'font') return
      if (el.attribs.size) {
        return {
          kind: 'mark',
          type: 'class',
          data: {
            class: `size-${el.attribs.size}`,
          },
          nodes: next(el.children),
        }
      }
      if (el.attribs.color) {
        return {
          kind: 'mark',
          type: 'color',
          data: {
            color: el.attribs.color,
          },
          nodes: next(el.children),
        }
      }
      if (el.attribs.style && el.attribs.style.includes('bold')) {
        return {
          kind: 'mark',
          type: 'bold',
          nodes: next(el.children),
        }
      }
    },
  },
  // Handle lists
  {
    deserialize(el, next) {
      if (el.name !== 'ul' && el.name !== 'ol') return
      const nodes = next(
        el.children.filter(
          ({ data }) => !IGNORE_CONTENT.some(exp => exp.test(data)),
        ),
      )
      return {
        kind: 'block',
        type: BLOCK_TAGS[el.name],
        nodes,
      }
    },
  },
  // Handle tables
  {
    deserialize(el, next) {
      if (el.name !== 'table') return
      const shouldKeep = ({ data }) =>
        !IGNORE_CONTENT.some(exp => exp.test(data))
      const filterMap = child => ({
        ...child,
        children:
          child.children && child.children.filter(shouldKeep).map(filterMap),
      })
      const filteredChildren = filterMap(el).children
      const cols = filteredChildren.filter(({ name }) => name === 'col')
      const totalWidth = cols.reduce(
        (width, col) => width + parseInt(col.attribs.width, 10),
        0,
      )
      const widths = cols.map(
        ({ attribs: { width } }) =>
          Math.floor(parseInt(width, 10) / totalWidth * 10e4) / 10e4,
      )
      const rows = filteredChildren.reduce((rows, ele) => {
        if (ele.name === 'thead' || ele.name === 'tbody') {
          rows.push(...ele.children)
        }
        return rows
      }, [])
      return {
        data: {
          widths,
        },
        kind: 'block',
        type: 'table',
        nodes: next(rows),
      }
    },
  },
  // Handle table rows
  {
    deserialize(el, next) {
      if (el.name !== 'tr') return
      return {
        kind: 'block',
        type: 'table_row',
        nodes: next(el.children),
      }
    },
  },
  // Handle table rows
  {
    deserialize(el, next) {
      if (el.name !== 'td' && el.name !== 'th') return
      return {
        kind: 'block',
        type: 'table_cell',
        nodes: next(el.children),
      }
    },
  },
  // Handle default tags
  {
    deserialize(el, next) {
      if (!el.name || !BLOCK_TAGS[el.name]) return
      return {
        kind: 'block',
        type: BLOCK_TAGS[el.name],
        nodes: next(el.children),
      }
    },
  },
  // Handle img tag
  {
    deserialize(el) {
      if (el.name !== 'img') return
      return {
        kind: 'block',
        type: 'image',
        isVoid: true,
        data: {
          src: el.attribs.src,
          width: el.attribs.width,
          height: el.attribs.height,
          name: el.attribs.name,
        },
      }
    },
  },
  // Handle marks tag
  {
    deserialize(el, next) {
      if (!el.name) return
      const mark = MARK_TAGS[el.name]
      if (!mark) return
      return {
        kind: 'mark',
        type: mark,
        nodes: next(el.children),
      }
    },
  },
  {
    // Handle link tag
    deserialize(el, next) {
      if (el.name !== 'a') return
      return {
        kind: 'inline',
        type: 'link',
        nodes: next(el.children),
        data: {
          href: el.attribs.href,
        },
      }
    },
  },
  {
    deserialize(el) {
      if (el.type !== 'text') return
      return {
        kind: 'text',
        ranges: [
          {
            text: processText(el.data),
          },
        ],
      }
    },
  },
]

const serializer = new Html({ rules: RULES })

const Plugin = () => ({
  onPaste: (e, data, state) => {
    const isInTable = editTablePlugin.utils.isSelectionInTable(state)
    if (data.type === 'html') {
      const { document } = serializer.deserialize(data.html)
      return state.transform().insertFragment(document).apply()
    } else {
      const transform = state.transform()
      for (const line of processText(data.text).split('\n')) {
        // cannot insert a paragraph block into a table_cell
        isInTable
          ? transform.insertText(line)
          : transform.insertBlock({
              type: 'paragraph',
              nodes: [Text.createFromString(line)],
            })
      }
      return transform.apply()
    }
  },
})

export default Plugin



// WEBPACK FOOTER //
// ./src/components/Editor/plugins/RichContentPaste.js